import React, { useCallback, useEffect, useMemo, useState } from "react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import { getMyNotifications } from "../api/notificationsApi";
import NotificationDetailView from "./NotificationDetailView";
import NotificationInboxRow from "./NotificationInboxRow";
import "../styles/NotificationsPage.css";

const MOBILE_DETAIL_MQ = "(max-width: 900px)";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * Notifications inbox UI — used by /app/notifications and Settings detail pane.
 * No module permissions; any signed-in user can load their own inbox.
 */
export default function NotificationsInbox({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const isMobile = useMediaQuery(MOBILE_DETAIL_MQ);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getMyNotifications(200);
      const data = Array.isArray(res?.data) ? res.data : [];
      setRows(data);
      setAccountEmail(res?.meta?.email || "");
      setSelectedId((prev) => {
        if (!data.length) return null;
        if (prev != null && data.some((r) => Number(r.id) === Number(prev))) {
          return prev;
        }
        return null;
      });
    } catch (err) {
      setRows([]);
      setSelectedId(null);
      setError(err?.message || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isMobile || loading || !rows.length) return;
    setSelectedId((prev) => (prev != null ? prev : rows[0].id));
  }, [isMobile, loading, rows]);

  const selected = useMemo(
    () => rows.find((r) => Number(r.id) === Number(selectedId)) ?? null,
    [rows, selectedId],
  );

  const countLabel = useMemo(() => {
    if (loading) return "Loading…";
    if (!rows.length) return "No notifications";
    return `${rows.length} notification${rows.length === 1 ? "" : "s"}`;
  }, [loading, rows.length]);

  const detailOpen = selected != null;
  const showListOnMobile = isMobile ? !detailOpen : true;
  const showDetailOnMobile = isMobile ? detailOpen : true;

  return (
    <div
      className={`notif-inbox-shell${embedded ? " notif-inbox-shell--embedded" : ""}`}
    >
      <header className="notif-inbox-hero">
        <div className="notif-inbox-hero__text">
          <p className="notif-inbox-hero__eyebrow">Inbox</p>
          <h2 className="notif-inbox-hero__title">Notifications</h2>
          {!embedded ? (
            <p className="notif-inbox-hero__sub">
              Email copies sent to your account appear here — open any row to read the
              full message.
            </p>
          ) : null}
          {accountEmail ? (
            <p className="notif-inbox-hero__account">{accountEmail}</p>
          ) : null}
        </div>
        <div className="notif-inbox-hero__actions">
          <GlobalButton type="button" variant="ghost" onClick={load} disabled={loading}>
            Refresh
          </GlobalButton>
        </div>
      </header>

      <GlobalFeedback type="error" text={error} />

      <div className="notif-inbox-toolbar">
        <span className="notif-inbox-toolbar__count">{countLabel}</span>
      </div>

      {loading ? (
        <p className="notif-inbox-status">Loading notifications…</p>
      ) : rows.length === 0 ? (
        <div className="notif-inbox-empty">
          <strong>No notifications yet</strong>
          <p>
            When the system sends you an email (membership, tournament, payment updates,
            etc.), a copy will show up here.
          </p>
        </div>
      ) : (
        <div
          className={`notif-inbox-panes${detailOpen ? " notif-inbox-panes--detail-open" : ""}`}
        >
          {showListOnMobile ? (
            <div
              className="notif-inbox-list-pane"
              role="list"
              aria-label="Notification list"
            >
              {rows.map((row) => (
                <NotificationInboxRow
                  key={row.id}
                  item={row}
                  selected={Number(row.id) === Number(selectedId)}
                  onSelect={() => setSelectedId(row.id)}
                />
              ))}
            </div>
          ) : null}

          {showDetailOnMobile ? (
            <div className="notif-inbox-detail-pane">
              <NotificationDetailView
                item={selected}
                showBack={isMobile && detailOpen}
                onBack={() => setSelectedId(null)}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
